import { PlayerWithTracks, TimingState } from '@rhg/components/media/types';
import { useMediaPreferences } from '@rhg/providers/media-preferences';
import { isIOS } from '@rhg/utils/browser';
import { defaultLanguage } from '@rhg/utils/intl';
import { AudioEvent, VideoEvent } from '@rhg/utils/media';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentLanguage } from '../use-current-language';
import { MediaControls, MediaControlsHookProps } from './types';

export function useMediaControls({
    mediaEl,
    type,
    isInitiallyMuted,
    fullscreenContainerRef,
    shouldInterruptPlay,
}: MediaControlsHookProps): MediaControls {
    const audioEl = useMemo(() => mediaEl as HTMLAudioElement, [mediaEl]);
    const videoEl = useMemo(() => mediaEl as PlayerWithTracks, [mediaEl]);
    const [isMuted, setIsMuted] = useState(!!isInitiallyMuted);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { isShowingSubtitles, setIsShowingSubtitles } = useMediaPreferences();
    const [currentCue, setCurrentCue] = useState<VTTCue | undefined>(undefined);
    const [timingState, setTimingState] = useState<TimingState>({
        isPaused: true,
        duration: 0,
        remainingTime: 0,
        currentTime: 0,
        currentPercent: 0,
        bufferedPercent: 0,
    });
    const [isPausedByUserInteraction, setIsPausedByUserInteraction] =
        useState<boolean>();

    const play = useCallback(() => {
        if (mediaEl) {
            mediaEl.play();
        }
    }, [mediaEl]);

    const pause = useCallback(() => {
        if (mediaEl) {
            mediaEl.pause();
        }
    }, [mediaEl]);

    /**
     * There are cases where the audio/video should be interrupted by
     * an external event (e.g. a dialog open). This is controlled by the
     * `shouldInterruptPlay` param.
     */
    const hasRemainingTime = timingState.remainingTime !== 0;
    const hasStartedPlayback = timingState.currentTime > 0;
    useEffect(() => {
        /**
         * If this param is not passed at all, then ignore this effect.
         */
        if (typeof shouldInterruptPlay !== 'boolean') {
            return;
        }

        /**
         * If this param is set to `true`, then interrupt the audio/video
         * by calling the `pause` callback.
         */
        if (shouldInterruptPlay) {
            pause();
            return;
        }

        /**
         * If `shouldInterruptPlay` is set to `false` and the audio/video has
         * not ended, then we want to trigger the `play` callback in only two cases:
         *
         * 1. Either the audio/video has not yet started
         * 2. Or the user has not paused the audio/video on demand
         */
        if (
            !shouldInterruptPlay &&
            hasRemainingTime &&
            (!hasStartedPlayback || !isPausedByUserInteraction)
        ) {
            play();
            return;
        }
    }, [
        isPausedByUserInteraction,
        pause,
        play,
        shouldInterruptPlay,
        hasStartedPlayback,
        hasRemainingTime,
    ]);

    /**
     * Callback that handles click or keyboard events triggered by user interaction.
     * This is passed as a click/key down handler to the play/pause buttons.
     */
    const togglePlay = useCallback(() => {
        if (mediaEl) {
            const isPaused =
                typeof mediaEl.paused === 'boolean'
                    ? mediaEl.paused
                    : mediaEl.paused();
            if (isPaused) {
                setIsPausedByUserInteraction(false);
                mediaEl.play();
            } else {
                setIsPausedByUserInteraction(true);
                mediaEl.pause();
            }
        }
    }, [mediaEl]);

    const toggleSubtitles = useCallback(
        (forcedState?: boolean) => {
            setIsShowingSubtitles(
                typeof forcedState === 'undefined'
                    ? !isShowingSubtitles
                    : forcedState
            );
        },
        [isShowingSubtitles, setIsShowingSubtitles]
    );

    const toggleFullscreen = useCallback(() => {
        if (type === 'video') {
            if (fullscreenContainerRef && !isIOS()) {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                    setIsFullscreen(false);
                } else if (fullscreenContainerRef?.current?.requestFullscreen) {
                    fullscreenContainerRef.current?.requestFullscreen();
                    setIsFullscreen(true);
                }
            } else {
                if (videoEl.isFullscreen()) {
                    videoEl.exitFullscreen();
                    setIsFullscreen(false);
                } else {
                    videoEl?.requestFullscreen();
                    setIsFullscreen(true);
                }
            }
        }
    }, [fullscreenContainerRef, type, videoEl]);

    const toggleMuted = useCallback(
        (forcedState?: boolean) => {
            if (mediaEl) {
                const isMuted =
                    typeof mediaEl.muted === 'boolean'
                        ? mediaEl.muted
                        : mediaEl.muted();

                const setMuted = (newMuted: boolean) => {
                    if (typeof mediaEl.muted === 'boolean') {
                        mediaEl.muted = newMuted;
                    } else {
                        mediaEl.muted(newMuted);
                    }
                    setIsMuted(newMuted);
                };

                if (isMuted || forcedState === true) {
                    setMuted(false);
                } else {
                    setMuted(true);
                }
            }
        },
        [mediaEl]
    );

    const setTiming = useCallback(
        (newTime?: number) => {
            if (mediaEl) {
                const isPaused =
                    typeof mediaEl.paused === 'boolean'
                        ? mediaEl.paused
                        : mediaEl.paused();

                const duration =
                    typeof mediaEl.duration === 'number'
                        ? isNaN(mediaEl.duration)
                            ? 0
                            : mediaEl.duration
                        : mediaEl.duration();

                // Calculate current time or new time
                const currentTime =
                    newTime !== undefined
                        ? newTime
                        : typeof mediaEl.currentTime === 'number'
                        ? isNaN(mediaEl.currentTime)
                            ? 0
                            : mediaEl.currentTime
                        : mediaEl.currentTime();

                // Set media element time
                if (newTime !== undefined) {
                    if (typeof mediaEl.currentTime === 'number') {
                        mediaEl.currentTime = currentTime ?? 0;
                    } else {
                        mediaEl.currentTime(currentTime);
                    }
                }

                if (currentTime !== undefined && !isNaN(currentTime)) {
                    const _duration = duration ?? 0;
                    setTimingState({
                        isPaused,
                        duration: _duration,
                        remainingTime: _duration - currentTime,
                        currentTime,
                        currentPercent: _duration ? currentTime / _duration : 0,
                        bufferedPercent: 100, // TODO actually calculate and set this eventually
                    });
                }
            }
        },
        [mediaEl, setTimingState]
    );

    const setTime = useCallback(
        (newTime: number) => {
            if (
                !isNaN(newTime) &&
                newTime >= 0 &&
                newTime < Number.MAX_SAFE_INTEGER
            ) {
                setTiming(newTime);
            }
        },
        [setTiming]
    );

    const showCue = useCallback((cue?: VTTCue) => {
        setCurrentCue(cue);
    }, []);

    useEffect(() => {
        if (mediaEl) {
            if (type === 'video') {
                videoEl.on(Object.values(VideoEvent), () => {
                    setTiming();
                });
            } else {
                Object.values(AudioEvent).forEach((eventType) => {
                    audioEl.addEventListener(eventType, () => {
                        setTiming();
                    });
                });
            }
        }
    }, [audioEl, mediaEl, setTiming, type, videoEl]);

    const textTracks = useMemo(() => {
        if (type === 'video') {
            return videoEl?.textTracks_?.tracks_ ?? [];
        } else {
            return audioEl?.textTracks
                ? Array.from(audioEl?.textTracks) ?? []
                : [];
        }
    }, [type, audioEl?.textTracks, videoEl?.textTracks_?.tracks_]);

    const currentLanguage = useCurrentLanguage();

    // Subtitles https://stackoverflow.com/questions/23475335/subtitles-for-audio-with-track-how-to-display-the-subtitles
    useEffect(() => {
        setCurrentCue(undefined);
        const hasMatchingLanguageTrack = textTracks.some(
            (t) =>
                (t.kind === 'subtitles' || t.kind === 'captions') &&
                t.language === currentLanguage
        );

        textTracks.forEach((track) => {
            if (track.kind === 'subtitles' || track.kind === 'captions') {
                if (type === 'video') {
                    if (
                        !hasMatchingLanguageTrack &&
                        track.language === defaultLanguage
                    ) {
                        track.mode = 'hidden';
                    } else if (track.language === currentLanguage) {
                        track.mode = 'hidden';
                    } else {
                        track.mode = 'disabled';
                    }
                    track.addEventListener('cuechange', () => {
                        const activeCues = track.activeCues;
                        showCue(
                            activeCues && activeCues?.length > 0
                                ? (activeCues[0] as VTTCue)
                                : undefined
                        );
                    });
                } else {
                    track.mode = 'hidden';
                    track.addEventListener('cuechange', (e) => {
                        const activeCues = (e.target as TextTrack).activeCues;
                        showCue((activeCues?.[0] as VTTCue) ?? undefined);
                    });
                }
            }
        });
    }, [type, showCue, textTracks, textTracks?.length, currentLanguage]);

    return {
        isFullscreen,
        isMuted,
        isShowingSubtitles,
        isPaused: !!(mediaEl
            ? typeof mediaEl.paused === 'boolean'
                ? mediaEl.paused
                : mediaEl.paused()
            : isPausedByUserInteraction),
        hasSubtitles: textTracks.length > 0,
        currentCue,
        timingState,
        togglePlay,
        toggleSubtitles,
        toggleFullscreen,
        toggleMuted,
        setTime,
    };
}
